/**
 * @file useMembershipTracking - Membership Modal 的打点 Hook
 */
import {useCallback, useMemo} from 'react';
import type {LogParams} from '@/services/tracking';
import {useTracking} from '@/services/tracking/context';
import {TrackEvents} from '@/services/tracking/constant';
import {useMemberLogExt} from '@/hooks/memberLogExt';
import {useModalStore} from '@/store/useModalStore';
import type {MemberLevelItem} from '@/services/commerce/membership/interface';
import type {SubscriptionType} from '@/services/commerce/type';

interface UseMembershipTrackingOptions {
    selectedLevel?: MemberLevelItem | null;
    activeTab?: SubscriptionType;
}
export const useMembershipTracking = (options?: UseMembershipTrackingOptions) => {
    const {sendClickLog, sendShowLog} = useTracking();
    const memberLogExt = useMemberLogExt();
    const membershipModalSource = useModalStore((s) => s.membershipModalSource);
    const {selectedLevel, activeTab} = options ?? {};

    // 构建基础扩展参数
    const baseExt = useMemo(
        () => ({
            ...memberLogExt,
            ...(membershipModalSource && {
                // eslint-disable-next-line camelcase
                scene_key: membershipModalSource,
            }),
            ...(selectedLevel && {
                action: selectedLevel.orderType,
                // eslint-disable-next-line camelcase
                targeted_period: activeTab,
                // eslint-disable-next-line camelcase
                targeted_level: selectedLevel.memberLevel,
            }),
        }),
        [memberLogExt, membershipModalSource, selectedLevel, activeTab],
    );

    // 包装打点方法，自动注入公参
    const wrappedSendClickLog = useCallback(
        (params: Omit<LogParams, 'type'>) => {
            sendClickLog({
                page: TrackEvents.PAGE_MEMBERSHIP_SUBSCRIPTION_POP,
                ...params,
                ext: {
                    ...baseExt,
                    ...params.ext,
                },
            });
        },
        [sendClickLog, baseExt],
    );

    const wrappedSendShowLog = useCallback(
        (params: Omit<LogParams, 'type'>) => {
            sendShowLog({
                page: TrackEvents.PAGE_MEMBERSHIP_SUBSCRIPTION_POP,
                ...params,
                ext: {
                    ...baseExt,
                    ...params.ext,
                },
            });
        },
        [sendShowLog, baseExt],
    );

    return {
        sendClickLog: wrappedSendClickLog,
        sendShowLog: wrappedSendShowLog,
        source: membershipModalSource,
    };
};
